import { useEffect, useState } from "react";
import { KanbanSquare, Plus, LayoutList, Columns3, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { fmtCurrency, fmtDate, statusClass } from "../lib/format";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const STAGES = ["Prospect", "Qualified", "Demo Scheduled", "Proposal Sent", "Negotiation", "Won", "Lost"];
const STAGE_ACCENT = {
  Prospect: "border-l-slate-400", Qualified: "border-l-blue-400", "Demo Scheduled": "border-l-blue-500",
  "Proposal Sent": "border-l-brand", Negotiation: "border-l-indigo-600", Won: "border-l-emerald-500", Lost: "border-l-red-500",
};

function approvalLevel(d) {
  if (d <= 20) return "BDM Approval";
  if (d <= 30) return "Manager Approval";
  if (d <= 40) return "Director Approval";
  return "CEO Approval Required";
}

export default function Pipeline() {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("kanban");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = () => api.get("/opportunities").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ stage: "Prospect", probability: 20, value: "", discount: 0, category: "Injectable" });
    setEditing(null); setOpen(true);
  };
  const openEdit = (o) => { setForm({ ...o }); setEditing(o); setOpen(true); };

  const save = async () => {
    if (!form.title && !form.product) { toast.error("Add a product / title"); return; }
    const payload = { ...form, title: form.title || `${form.product} - ${form.clinic_name || ""}` };
    try {
      if (editing) await api.put(`/opportunities/${editing.id}`, payload);
      else await api.post("/opportunities", payload);
      toast.success(editing ? "Opportunity updated" : "Opportunity created");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const moveStage = async (id, stage) => {
    const probs = { Prospect: 20, Qualified: 40, "Demo Scheduled": 60, "Proposal Sent": 75, Negotiation: 90, Won: 100, Lost: 0 };
    setRows((rs) => rs.map((r) => r.id === id ? { ...r, stage, probability: probs[stage] } : r));
    try {
      await api.put(`/opportunities/${id}`, { stage, probability: probs[stage], invoice_paid: stage === "Won" });
    } catch { toast.error("Update failed"); load(); }
  };

  const byStage = (s) => rows.filter((r) => r.stage === s);
  const stageTotal = (s) => byStage(s).reduce((a, r) => a + Number(r.value || 0), 0);

  return (
    <div>
      <PageHeader title="Sales Pipeline" subtitle="Track opportunities from prospect to won" icon={KanbanSquare}>
        <div className="flex rounded-lg border border-slate-200 p-0.5 bg-white">
          <button onClick={() => setView("kanban")} data-testid="view-kanban"
            className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 ${view === "kanban" ? "bg-navy-900 text-white" : "text-slate-500"}`}>
            <Columns3 className="h-4 w-4" /> Board
          </button>
          <button onClick={() => setView("list")} data-testid="view-list"
            className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 ${view === "list" ? "bg-navy-900 text-white" : "text-slate-500"}`}>
            <LayoutList className="h-4 w-4" /> List
          </button>
        </div>
        <Button size="sm" onClick={openCreate} className="bg-navy-900 hover:bg-navy-800" data-testid="add-opportunity-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New Opportunity
        </Button>
      </PageHeader>

      {view === "kanban" ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STAGES.map((stage) => (
            <div key={stage} className="w-72 shrink-0"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragId) { moveStage(dragId, stage); setDragId(null); } }}
              data-testid={`column-${stage.toLowerCase().replace(/ /g, "-")}`}>
              <div className="flex items-center justify-between px-1 mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-display font-semibold text-sm text-navy-900">{stage}</span>
                  <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{byStage(stage).length}</span>
                </div>
                <span className="text-xs text-slate-400">{fmtCurrency(stageTotal(stage))}</span>
              </div>
              <div className="bg-slate-100/60 rounded-xl p-2 min-h-[120px] space-y-2">
                {byStage(stage).map((o) => (
                  <div key={o.id} draggable onDragStart={() => setDragId(o.id)} onClick={() => openEdit(o)}
                    data-testid={`kanban-card-${o.id}`}
                    className={`bg-white rounded-lg border border-slate-200 border-l-4 ${STAGE_ACCENT[stage]} p-3 shadow-sm hover:shadow-md cursor-pointer transition-shadow`}>
                    <div className="text-sm font-semibold text-navy-900 leading-snug">{o.product}</div>
                    <div className="text-xs text-slate-400 mt-0.5 truncate">{o.clinic_name}</div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm font-bold text-navy-900">{fmtCurrency(o.value)}</span>
                      {Number(o.discount) > 0 && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${Number(o.discount) > 40 ? "bg-red-50 text-red-600 border-red-200" : "bg-amber-50 text-amber-600 border-amber-200"}`}>
                          {o.discount}% off
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1.5">{o.bdm_name} · {o.probability}%</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 bg-slate-50/60">
              {["Opportunity", "Clinic", "BDM", "Stage", "Value", "Discount", "Prob.", "Close Date"].map((h) => (
                <th key={h} className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-3 whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} onClick={() => openEdit(o)} className="border-b border-slate-50 hover:bg-slate-50/70 cursor-pointer" data-testid={`opp-row-${o.id}`}>
                  <td className="px-4 py-3 font-medium text-navy-900">{o.product}</td>
                  <td className="px-4 py-3 text-slate-600">{o.clinic_name}</td>
                  <td className="px-4 py-3 text-slate-600">{o.bdm_name}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2.5 py-1 rounded-full border ${statusClass(o.stage)}`}>{o.stage}</span></td>
                  <td className="px-4 py-3 font-semibold text-navy-900">{fmtCurrency(o.value)}</td>
                  <td className="px-4 py-3 text-slate-600">{o.discount || 0}%</td>
                  <td className="px-4 py-3 text-slate-600">{o.probability}%</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(o.close_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "New"} Opportunity</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <Field label="Product"><Input value={form.product || ""} onChange={(e) => setForm({ ...form, product: e.target.value })} data-testid="opp-product" /></Field>
            <Field label="Clinic"><Input value={form.clinic_name || ""} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} data-testid="opp-clinic" /></Field>
            <Field label="Category">
              <Select value={form.category || ""} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="opp-category"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>{["Injectable", "Device", "Skincare", "Consumables"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Brand"><Input value={form.brand || ""} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></Field>
            <Field label="Value (NZD)"><Input type="number" value={form.value ?? ""} onChange={(e) => setForm({ ...form, value: Number(e.target.value) })} data-testid="opp-value" /></Field>
            <Field label="Discount (%)"><Input type="number" value={form.discount ?? 0} onChange={(e) => setForm({ ...form, discount: Number(e.target.value) })} data-testid="opp-discount" /></Field>
            <Field label="Stage">
              <Select value={form.stage || ""} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger data-testid="opp-stage"><SelectValue placeholder="Stage" /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Probability (%)"><Input type="number" value={form.probability ?? ""} onChange={(e) => setForm({ ...form, probability: Number(e.target.value) })} /></Field>
            <Field label="Expected Close"><Input type="date" value={form.close_date || ""} onChange={(e) => setForm({ ...form, close_date: e.target.value })} /></Field>
            <Field label="Competitor"><Input value={form.competitor || ""} onChange={(e) => setForm({ ...form, competitor: e.target.value })} /></Field>
            <Field label="BDM"><Input value={form.bdm_name || ""} onChange={(e) => setForm({ ...form, bdm_name: e.target.value })} /></Field>
            <Field label="Region"><Input value={form.region || ""} onChange={(e) => setForm({ ...form, region: e.target.value })} /></Field>
            <div className="sm:col-span-2"><Field label="Notes"><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>
          </div>
          {Number(form.discount) > 0 && (
            <div className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 border ${Number(form.discount) > 40 ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
              <AlertTriangle className="h-4 w-4" /> Discount routing: <strong>{approvalLevel(Number(form.discount))}</strong>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-navy-900 hover:bg-navy-800" data-testid="save-opp-btn">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block">{label}</label>{children}</div>;
}
